// @ts-check
import { defineStore } from 'pinia'
import {
  DiscoveryResponse,
  type DiscoveryEndpoint,
  type DiscoveryEndpointAdditionalInfo,
  type DiscoveryFeature
} from '../services/discovery'
import localStorageService from '../stores/localStorageService'
import { isAuthenticated } from '../authentication'
import { DiscoveryBranding } from '../services/discovery'

export enum MapType {
  Google,
  HereMaps,
  None
}

const store = localStorageService.getStore('state')

export type AppStateType = {
  discovery: DiscoveryResponse | null
  initialised: boolean
  accessToken: string
  userName: string
  emailAddress: string
  isAuthenticated: boolean
  //jobSearchRequest: JobSearchRequest;
  //diaryEventsRequest: DiaryEventsRequest;
  timesheetOnboardingStatus: boolean
  onBoarded: boolean
  installAppExpiry: Date
  isOnline: boolean
  profileSaved: boolean
  hasLoggedIn: boolean
  firstName: string
  lastUpdateCheck: Date
  checkForUpdate: boolean
  hasAcceptedTerms: boolean
  jobOffers: number
  mapService: MapType
  googleApiKey: string
}

export const useAppState = defineStore('appState', {
  state: (): AppStateType => {
    return {
      discovery: null,
      initialised: true,
      accessToken: '',
      userName: '',
      emailAddress: '',
      isAuthenticated: false,
      //jobSearchRequest: {} as JobSearchRequest,
      //diaryEventsRequest: {} as DiaryEventsRequest,
      timesheetOnboardingStatus: false,
      onBoarded: false,
      installAppExpiry: new Date(),
      isOnline: false,
      profileSaved: false,
      hasLoggedIn: false,
      firstName: '',
      lastUpdateCheck: new Date(),
      checkForUpdate: false,
      hasAcceptedTerms: false,
      jobOffers: 0,
      mapService: MapType.None,
      googleApiKey: ''
    }
  },
  getters: {
    getAccessToken: (state): string => {
      //console.log(mainAuth.client().accessToken)
      //return mainAuth.client().accessToken
      return state.accessToken
    },
    getIsAuthenticated: async (state): Promise<boolean> => {
      return await isAuthenticated()
    },
    //getUrl: (state): string => mainAuth.client().appUrl,
    //getUserName: (state): string => mainAuth.client().userProfile.name,
    //getEmailAddress: (state): string => mainAuth.client().userProfile.email,
    getDiscovery: async (): Promise<DiscoveryResponse> => {
      return (await store.getItem('discovery')) as DiscoveryResponse
    },
    getBranding: async (): Promise<DiscoveryBranding> => {
      return (await store.getItem('branding')) as DiscoveryBranding
    },
    //jobSearchRequest: (state): JobSearchRequest => state.jobSearchRequest,
    //diaryEventsRequest: (state): DiaryEventsRequest => state.diaryEventsRequest,
    getTimesheetOnboardingStatus: (state): boolean => state.timesheetOnboardingStatus,
    getOnBoarded: (state): boolean => state.onBoarded,
    getInstallAppExpiry: (state): Date => state.installAppExpiry,
    getFirstName: (state): string => state.firstName,
    getLastUpdateCheck: (state): Date => state.lastUpdateCheck,
    getCheckForUpdate: (state): boolean => {
      if (state.lastUpdateCheck) {
        const today = new Date()
        const lastUpdate = state.lastUpdateCheck as Date
        const diffMs = lastUpdate.getTime() - today.getTime()
        const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000) // minutes

        return diffMins >= 5
      }

      return true
    },
    getHasAcceptedTerms: (state): boolean => state.hasAcceptedTerms,
    getEndPoint: (state): ((endPointName: string) => DiscoveryEndpoint | undefined) => {
      return (endPointName: string): DiscoveryEndpoint | undefined =>
        state.discovery?.endpoints.find((item) => item.name === endPointName)
    },
    getEndPointAdditionalInfo: (state: AppStateType) => {
      return (endPointName: string, key: string): DiscoveryEndpointAdditionalInfo | undefined => {
        if (state.discovery) {
          return state.discovery.endpoints
            .filter((item) => item.name === endPointName)[0]
            .additionalInfo.filter((item) => item.key === key)[0]
        }
      }
    },
    getFeature: (state: AppStateType) => {
      return (uniqueId: string): DiscoveryFeature | undefined => {
        if (state.discovery) {
          return state.discovery.features.filter((item) => {
            return item.uniqueId === uniqueId
          })[0]
        }
      }
    },
    //This is an example of a getter that takes a parameter
    //const store = useStore()
    //const isFeatureEnabled = store.featureEnabled('someUniqueId')
    //It used to be like this in VUEX
    //featureEnabled: (state: AppStateType, uniqueId: string) => {
    getFeatureEnabled: (state: AppStateType) => {
      return (uniqueId: string): boolean => {
        const features = state.discovery?.features.filter((item) => {
          return item.uniqueId === uniqueId
        })

        if (features && features.length > 0) {
          return features[0].permission == 'HasAccess'
        }

        return false
      }
    },
    getIsOnline: (): boolean => navigator.onLine,
    getProfileSaved: (state): boolean => state.profileSaved,
    getHasLoggedIn: (state): boolean => state.hasLoggedIn,
    getJobOffers: (state): number => state.jobOffers,
    getMapService: (state): MapType => state.mapService
  },
  actions: {
    async initialiseStore() {
      const keys = Object.keys(this.$state)

      try {
        await Promise.all(
          keys.map(async (key) => {
            const dbItem = await store.getItem(key)
            if (dbItem) {
              ;(this.$state as any)[key] = dbItem
            } else {
              await store.setItem(key, (this.$state as any)[key])
            }
          })
        )
      } catch (e) {
        console.log(e)
      }
    },
    retrieveAccessToken() {
      //return mainAuth.client().getAccessToken(false)
    },
    retrieveAccessTokenForceRefresh() {
      //return mainAuth.client().getAccessToken(true)
    },
    async UpdateDiscovery(discovery: DiscoveryResponse) {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          store.setItem('discovery', discovery)
          store.setItem('branding', discovery.branding)
          resolve()
        }, 1000)
      })
    },
    //   UpdateJobSearchRequest(state, jobSearchRequest: JobSearchRequest) {
    //     state.jobSearchRequest = jobSearchRequest;
    //   },
    //   UpdateDiaryEventsRequest(state, diaryEventsRequest: DiaryEventsRequest){
    //       state.diaryEventsRequest = diaryEventsRequest;
    //   },
    UpdateTimesheetOnboardingStatus(val: boolean) {
      this.$state.timesheetOnboardingStatus = val
    },
    UpdateOnBoarded(val: boolean) {
      this.$state.onBoarded = val
    },
    UpdateInstallAppExpiry(val: Date) {
      this.$state.installAppExpiry = val
    },
    UpdateProfileSaved(val: boolean) {
      this.$state.profileSaved = val
    },
    UpdateHasLoggedIn(val: boolean) {
      this.$state.hasLoggedIn = val
    },
    UpdateFirstName(val: string) {
      this.$patch({ firstName: val })
    },
    UpdateLastUpdateCheck(date: Date) {
      this.$state.lastUpdateCheck = date
    },
    UpdateHasAcceptedTerms(val: boolean) {
      this.$state.hasAcceptedTerms = val
    },
    UpdateJobOffers(val: number) {
      this.$state.jobOffers = val
    },
    UpdateMapService(val: MapType) {
      this.$state.mapService = val
    }
  }
})
